import { serializeError } from 'serialize-error';
import type { atsvb } from 'audio-effects-sdk';
import type { LoggerFactory } from '../../../../../types/container';
import type Logger from '../../../../Logger';
import type { AudioTrackProcessor } from '../AudioTrackProcessor';
import { TEN_SECONDS_IN_MS, DEBOUNCE_LIMIT, ASDK_VERSION } from '../../../../../constants/common';
import type ConfigService from '../../../../../config/ConfigService';
import OperationError from '../../../../../errors/OperationError';
import { withTimeout } from '../../../../../helpers/withTimeout';

enum ErrorType {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error'
}
enum ErrorCode {
  PERFORMANCE_STOP = 1001,
  REDUCE_LATENCY = 1002,
  MODEL_LOAD_FAILED = 1010,
  PROCESSOR_INIT_ISSUE = 1020,
  AUTH_ISSUE = 1030,
  SUPPORT_ISSUE = 1040,
  PROCESSING_PROGRESS = 1060,
}
enum ErrorEmitter {
  ATSVB = 'atsvb',
  STREAM_PROCESSOR = 'stream_processor',
  ML_INFERENCE = 'ml_inference',
  MODEL = 'model',
  WORKLET = 'worklet',
  WORKER = 'worker',
  MODEL_LOADER = 'model_loader',
}

interface ErrorObject {
  message: string;
  type: ErrorType;
  code?: ErrorCode;
  emitter?: ErrorEmitter;
  cause?: Error;
  data?: unknown;
}

declare global {
  interface Window {
    atsvb: typeof atsvb;
  }
}

const noop = (): void => { /* no operation */ };

export interface AudioTrackProcessorParams {
  asdkCustomerId: string;
  config: ConfigService;
  loggerFactory: LoggerFactory;
  onModelsLoaded?: () => void;
  onError?: () => void;
}

export class ASDKTrackProcessor implements AudioTrackProcessor {
  readonly #atsvb: atsvb;

  readonly #logger: Logger;

  #originalTrack: MediaStreamTrack | null = null;

  readonly #debounceCounters = new Map<number, number>();

  #track?: MediaStreamTrack;

  readonly type = 'asdk';

  #onError: (reason: string, error: Error | undefined) => void;

  constructor(params: AudioTrackProcessorParams) {
    const { loggerFactory } = params;
    this.#logger = loggerFactory('ASDKTrackProcessor');
    this.#onError = typeof params.onError === 'function' ? params.onError : noop;

    this.#atsvb = new window.atsvb(params.asdkCustomerId);

    const staticFilesPath = params.config.get('staticFilesPath');
    const {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      localDir, version, sdk_url, api_url,
    } = params.config.get('asdk');

    let pathToWasm = `https://effectssdk.ai/sdk/audio/dev/${ version ?? ASDK_VERSION }`;

    if (localDir) {
      const url = new URL(`${ localDir }/${ version ?? ASDK_VERSION }`, staticFilesPath);
      pathToWasm = url.href;
    }

    const asdkParams: Record<string, unknown> = {
      preset: 'balanced',
      sample_rate: 48_000,
      api_url,
      wasmPaths: {
        'ort-wasm.wasm': `${ pathToWasm }/ort-wasm.wasm`,
        'ort-wasm-simd.wasm': `${ pathToWasm }/ort-wasm-simd.wasm`,
      },
    };

    const shouldLoadFromLocal = !sdk_url && localDir;
    const sdkUrl = shouldLoadFromLocal ? `${ pathToWasm }/` : sdk_url;

    if (shouldLoadFromLocal) {
      this.#logger.warn('Using static ASDK path due to missing config', {
        case: 'Denoiser',
        type: 'asdk',
        ...params.config.get('asdk'),
        sdkUrl: asdkParams.sdk_url,
      });
    }

    if (sdkUrl) {
      asdkParams.sdk_url = sdkUrl;
    }

    this.#atsvb.config(asdkParams);

    this.#atsvb.onError(this.onError.bind(this));
  }

  setOnError(onError: (reason: string, error: Error | undefined) => void): void {
    this.#onError = onError;
  }

  @withTimeout(TEN_SECONDS_IN_MS)
  async init(): Promise<void> {
    await this.#atsvb.preload();
  }

  release(): void {
    this.#atsvb.stop();
    this.#atsvb.clear();
  }

  get isProcessing(): boolean {
    return !!this.#track;
  }

  async processTrack(track: MediaStreamTrack): Promise<MediaStreamTrack> {
    if (this.#originalTrack === track || track.readyState === 'ended') {
      return this.#atsvb.getStream()?.getAudioTracks()[0] || track;
    }

    this.stopProcessing();

    try {
      const outTrackPromise = new Promise<MediaStreamTrack>((resolve, reject) => {
        this.#atsvb.onReady = () => {
          try {
            const newStream = this.#atsvb.getStream();
            const outTrack = newStream?.getAudioTracks()[0];

            if (!outTrack) {
              resolve(track);

              return;
            }

            this.#originalTrack = track;
            this.#track = outTrack;

            resolve(outTrack);
            this.#atsvb.run();
          } catch (error) {
            reject(error as Error);
          } finally {
            this.#atsvb.onReady = undefined;
          }
        };
      });

      this.#atsvb.useStream(new MediaStream([track]));

      const outTrack = await outTrackPromise;

      this.#logger.info('processTrack()', {
        case: 'Denoiser',
        type: this.type,
        track: track.id,
      });

      return outTrack;
    } catch (error) {
      this.#logger.error('processTrack()', {
        error: serializeError(error),
        type: this.type,
        case: 'Denoiser',
        track,
      });

      this.#onError('ASDK processTrack() error', error as Error);
      this.#onError = noop;

      throw new OperationError('asdk_process_track_failed', error);
    }
  }

  stopProcessing(): void {
    try {
      this.#atsvb.stop();
    } catch (error) {
      this.#logger.error('stopProcessing()', {
        type: this.type,
        case: 'Denoiser',
        error: serializeError(error),
      });
    } finally {
      this.#originalTrack = null;
    }
  }

  getUpdatedDebounceCounter(errorCode: number): number {
    let value = this.#debounceCounters.get(errorCode) ?? 0;

    value += 1;
    this.#debounceCounters.set(errorCode, value);

    setTimeout(() => {
      const currentValue = this.#debounceCounters.get(errorCode) ?? 0;

      if (currentValue <= 0) {
        this.#debounceCounters.set(errorCode, 0);

        return;
      }

      this.#debounceCounters.set(errorCode, currentValue - 1);
    }, TEN_SECONDS_IN_MS);

    return value;
  }

  onError(error: ErrorObject): void {
    const {
      message, type, cause, ...rest
    } = error;

    if (type === ErrorType.ERROR) {
      this.#logger.error(message, {
        error: serializeError(cause),
        cause,
        ...rest,
        type: this.type,
        case: 'Denoiser',
      });

      if (rest.code === ErrorCode.REDUCE_LATENCY || rest.code === ErrorCode.PERFORMANCE_STOP) {
        const debounceCounter = this.getUpdatedDebounceCounter(rest.code);

        if (debounceCounter > DEBOUNCE_LIMIT) {
          this.#debounceCounters.set(rest.code, 0);
          this.#onError(`ASDK performance issue(${ rest.code }): ${ message }`, cause);
          this.#onError = noop;
        }
      } else {
        this.#onError(`ASDK runtime error: ${ message }`, cause);
        this.#onError = noop;
      }
    }

    if (type === ErrorType.WARNING) {
      this.#logger.warn(message, {
        ...rest,
        type: this.type,
        case: 'Denoiser',
      });
    }

    if (type === ErrorType.INFO) {
      this.#logger.debug(message, {
        ...rest,
        type: this.type,
        case: 'Denoiser',
      });
    }
  }
}
